import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Grid3x3 } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { apiClient } from "@/lib/api";
import { PageHeader, EmptyState, Spinner } from "@/components/shared/ui";

export default function Units() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [types, setTypes] = useState(["A", "B", "C"]);
  const [selectedType, setSelectedType] = useState("A");
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({});

  useEffect(() => {
    apiClient
      .get("/units/types")
      .then((data) => {
        const tps = Array.isArray(data) ? data.map((d) => (typeof d === "string" ? d : d.type)) : [];
        if (tps.length) {
          setTypes(tps);
          if (!tps.includes(selectedType)) setSelectedType(tps[0]);
        }
      })
      .catch(() => {});
    (async () => {
      const c = {};
      for (const tp of ["A", "B", "C"]) {
        try {
          const res = await apiClient.get(`/units?type=${tp}&limit=1&page=1`);
          c[tp] = res.total || 0;
        } catch {
          c[tp] = 0;
        }
      }
      setCounts(c);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUnits = useCallback(
    async (p = 1, append = false) => {
      try {
        if (p > 1) setLoadingMore(true);
        const data = await apiClient.get(`/units?page=${p}&limit=100&search=${search}&type=${selectedType}`);
        const fetched = data.units || [];
        setUnits((prev) => {
          if (!append) return fetched;
          const seen = new Set(prev.map((u) => u.unit_number));
          return [...prev, ...fetched.filter((u) => !seen.has(u.unit_number))];
        });
        setTotalPages(data.total_pages || 1);
        setTotal(data.total || 0);
        setPage(p);
      } catch {
        /* ignore */
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [search, selectedType]
  );

  useEffect(() => {
    setLoading(true);
    setPage(1);
    fetchUnits(1, false);
  }, [search, selectedType, fetchUnits]);

  const typeLabel = (tp) => t(`type_${tp.toLowerCase()}`) || tp;

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader title={t("site_units")} subtitle={`${total} ${t("units_loaded")}`} />

      <div className="mb-4 flex flex-wrap gap-2.5">
        {types.map((tp) => {
          const active = tp === selectedType;
          return (
            <button
              key={tp}
              data-testid={`type-tab-${tp}`}
              onClick={() => {
                setSelectedType(tp);
                setSearch("");
              }}
              className={`flex items-center gap-2 rounded-xl border px-5 py-2.5 text-sm font-semibold transition-colors ${
                active ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
              }`}
            >
              {typeLabel(tp)}
              <span className={`rounded-md px-1.5 py-0.5 text-xs ${active ? "bg-white/20" : "bg-slate-100 text-slate-500"}`}>{counts[tp] || 0}</span>
            </button>
          );
        })}
      </div>

      <div className="mb-6 flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3">
        <Search className="h-4 w-4 text-slate-400" />
        <input
          data-testid="units-search-input"
          className="h-11 flex-1 bg-transparent text-sm outline-none"
          placeholder={t("search_unit")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search ? (
          <button data-testid="clear-search-btn" onClick={() => setSearch("")}>
            <X className="h-4 w-4 text-slate-400" />
          </button>
        ) : null}
      </div>

      {loading ? (
        <div className="py-20 text-center">
          <Spinner className="mx-auto h-8 w-8 text-slate-400" />
        </div>
      ) : units.length === 0 ? (
        <EmptyState icon={Grid3x3} title={t("no_units_found")} />
      ) : (
        <>
          <div className="grid grid-cols-4 gap-2.5 sm:grid-cols-6 lg:grid-cols-10">
            {units.map((u) => (
              <button
                key={u.unit_number}
                data-testid={`unit-block-${u.unit_number}`}
                onClick={() => navigate(`/units/${u.unit_number}`)}
                className="relative flex aspect-square items-center justify-center rounded-xl border border-slate-200 bg-white text-lg font-bold text-slate-700 shadow-sm transition-all hover:-translate-y-0.5 hover:border-[var(--brand)] hover:shadow-md"
              >
                <span className="absolute end-1.5 top-1.5 rounded bg-slate-100 px-1 text-[9px] font-bold text-slate-500">{u.type || "A"}</span>
                {u.number}
              </button>
            ))}
          </div>
          {page < totalPages ? (
            <div className="mt-6 text-center">
              <button
                onClick={() => fetchUnits(page + 1, true)}
                disabled={loadingMore}
                className="rounded-xl border border-slate-200 bg-white px-6 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-60"
              >
                {loadingMore ? <Spinner className="mx-auto h-4 w-4" /> : "Load more"}
              </button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
